import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'

/**
 * Roteador de ~90 linhas.
 *
 * O console tem sete telas, nenhuma aninhada e nenhuma com parâmetro de rota.
 * Uma biblioteca de roteamento aqui seria mais configuração do que este arquivo
 * inteiro, e traria um vocabulário a mais para quem for mexer.
 *
 * History API, não hash: estas URLs aparecem em ticket e em link colado no
 * chat, e `/empresas` é o que se espera ler. Exige fallback de SPA no servidor
 * que publica o bundle — `vite dev` e `vite preview` já fazem.
 */

const CHANGE_EVENT = 'hermes:navigate'

/**
 * Prefixo da experiência ativa: `''` na Black Ghost, `/app` no cliente.
 *
 * As telas compartilhadas (Detecções, Logs, Processo) montam endereços canônicos
 * — `/logs/…`, `/processes/…` — e não sabem em qual experiência estão. O
 * roteador acrescenta o prefixo ao navegar e o remove ao ler, então a mesma
 * tela produz `/app/logs/…` para o cliente sem conhecer o prefixo. Quem escolhe
 * o prefixo é a casca, a partir do `scope` de `/auth/me`.
 *
 * Estado de módulo de propósito: os construtores de endereço
 * (`lib/logs/investigation.ts`) são funções puras chamadas fora de componentes.
 */
let routeBase = ''

export function setRouteBase(base: string): void {
  routeBase = base
}

/** Endereço canônico → endereço real, com o prefixo da experiência. */
export function withBase(to: string): string {
  if (routeBase === '' || !to.startsWith('/')) return to
  if (to === routeBase || to.startsWith(`${routeBase}/`) || to.startsWith(`${routeBase}?`)) return to
  return to === '/' ? routeBase : to.startsWith('/?') ? `${routeBase}${to.slice(1)}` : `${routeBase}${to}`
}

/**
 * Endereço real → canônico. `null` quando o endereço está FORA da experiência
 * ativa (um `/empresas` aberto por um usuário de cliente): a casca mostra "não
 * encontrado" em vez de renderizar a tela da outra experiência.
 */
function stripBase(path: string): string | null {
  if (routeBase === '') return path
  if (path === routeBase || path === `${routeBase}/`) return '/'
  if (path.startsWith(`${routeBase}/`)) return path.slice(routeBase.length)
  return null
}

/** SSR e os checks rodam sem `window`; a raiz é a resposta correta ali. */
function currentLocation(): { path: string; search: string } {
  if (typeof window === 'undefined') return { path: '/', search: '' }
  return { path: window.location.pathname, search: window.location.search }
}

export function navigate(target: string, options: { replace?: boolean } = {}): void {
  if (typeof window === 'undefined') return
  const to = withBase(target)

  const current = `${window.location.pathname}${window.location.search}`
  if (to === current) return

  if (options.replace === true) window.history.replaceState(null, '', to)
  else window.history.pushState(null, '', to)

  // `pushState` não dispara `popstate`: o aviso é nosso.
  window.dispatchEvent(new Event(CHANGE_EVENT))
  window.scrollTo(0, 0)
}

function useLocation(): { path: string; search: string } {
  const [location, setLocation] = useState(currentLocation)

  useEffect(() => {
    const sync = (): void => setLocation(currentLocation())
    window.addEventListener('popstate', sync)
    window.addEventListener(CHANGE_EVENT, sync)
    return () => {
      window.removeEventListener('popstate', sync)
      window.removeEventListener(CHANGE_EVENT, sync)
    }
  }, [])

  return location
}

/** O caminho CANÔNICO da experiência ativa (sem o prefixo `/app`). */
export function usePath(): string {
  const { path } = useLocation()
  return stripBase(path) ?? path
}

/**
 * O caminho canônico, ou `null` quando o endereço não pertence à experiência
 * ativa. É o que a casca usa para nunca renderizar a tela da outra experiência.
 */
export function useRoutePath(): { path: string | null; raw: string } {
  const { path } = useLocation()
  return { path: stripBase(path), raw: path }
}

/**
 * Um parâmetro da query.
 *
 * Existe para uma coisa só: chegar numa listagem já filtrada — "as licenças
 * desta empresa" a partir da tela de empresas. O filtro fica na URL para que o
 * operador possa colar o endereço num ticket e o colega abrir a mesma tela.
 */
export function useQueryParam(name: string): string | null {
  const { search } = useLocation()
  return new URLSearchParams(search).get(name)
}

/**
 * Todos os parâmetros da query, reativos.
 *
 * A tela de investigação lê um punhado deles de uma vez — o recorte do
 * histórico mora na URL para ser colável num ticket (§7). Um único
 * `URLSearchParams` evita reassinar a localização uma vez por parâmetro.
 */
export function useSearchParams(): URLSearchParams {
  const { search } = useLocation()
  // Mesma instância enquanto a query não muda: quem deriva filtros com
  // `useMemo([search])` refaria a busca a cada render — e cada resposta
  // provoca um render, o que vira laço de requisições.
  return useMemo(() => new URLSearchParams(search), [search])
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; children: ReactNode }

/**
 * Âncora de verdade.
 *
 * `<a href>` real para que abrir em nova aba, copiar o endereço e o cursor do
 * navegador funcionem. O clique só é interceptado quando é simples e sem
 * modificador — ctrl/cmd/shift e o botão do meio seguem para o navegador.
 */
export function Link({ href: target, children, onClick, ...rest }: LinkProps) {
  const href = withBase(target)
  const handle = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event)
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      event.preventDefault()
      navigate(href)
    },
    [href, onClick],
  )

  return (
    <a href={href} onClick={handle} {...rest}>
      {children}
    </a>
  )
}
